<template>
  <d-container fluid class="main-content-container px-4 kpi-targets">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('kpi.workersGoals') }}</span>
        <h3 class="page-title">{{ $t('kpi.kpiImplementation') }}</h3>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="kpiRealizations-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-datepicker
          v-model="filters.period"
          :monday-first="true"
          format="MMMM yyyy"
          :placeholder="$t('ui.chooseDate')"
          minimum-view="month"
          class="mr-auto w-25"
          :language="translationsForDatePicker" />

        <d-button
          theme="info"
          outline
          :class="{ 'text-nowrap': true, 'ml-2': true }"
          @click.prevent="validateFiltersForm">
          {{ $t('ui.search') }}
        </d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('users.worker') }}</th>
            <th scope="col" class="border-0" style="width: 130px;"><small>{{ $t('kpi.contactsNumberRegardingNewOrders') }}</small></th>
            <th scope="col" class="border-0" style="width: 130px;"><small>{{ $t('kpi.numberOfStartedOrders') }}</small></th>
            <th scope="col" class="border-0" style="width: 130px;"><small>{{ $t('kpi.amountOfStartedOrders') }}</small></th>
            <th scope="col" class="border-0" style="width: 130px;"><small>{{ $t('kpi.numberOfStartedOffers') }}</small></th>
            <th scope="col" class="border-0" style="width: 130px;"><small>{{ $t('kpi.numberOfNewCustomers') }}</small></th>
            <th scope="col" class="border-0" style="width: 130px;"><small>{{ $t('kpi.numberOfNewContacts') }}</small></th>
            <!--<th scope="col" class="border-0" style="width: 80px;"><small>{{ $t('kpi.mean') }}</small></th>-->
            <!--<th scope="col" class="border-0" style="width: 100px;"><small>{{ $t('kpi.sum') }}</small></th>-->
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(data, index) of kpiRealizations.data"
            :key="'target-' + index">
            <td class="align-middle">
              {{ data.target.user_name }}
            </td>
            <td>
              {{ data.new_orders_contacts_count }} <small>/ {{ data.target.new_orders_contacts_count }}</small><br>
              <small :class="{
                'text-success': data.new_orders_contacts_count_percent >= 100,
                'text-danger': data.new_orders_contacts_count_percent < 100
              }">
                {{ data.new_orders_contacts_count_percent }}%
              </small>
            </td>
            <td>
              {{ data.started_orders_count }} <small>/ {{ data.target.started_orders_count }}</small><br>
              <small :class="{
                'text-success': data.started_orders_count_percent >= 100,
                'text-danger': data.started_orders_count_percent < 100
              }">
                {{ data.started_orders_count_percent }}%
              </small>
            </td>
            <td>
              {{ data.started_orders_amount }} <small>/ {{ data.target.started_orders_amount }}</small><br>
              <small :class="{
                'text-success': data.started_orders_amount_percent >= 100,
                'text-danger': data.started_orders_amount_percent < 100
              }">
                {{ data.started_orders_amount_percent }}%
              </small>
            </td>
            <td>
              {{ data.started_offers_count }} <small>/ {{ data.target.started_offers_count }}</small><br>
              <small :class="{
                'text-success': data.started_offers_count_percent >= 100,
                'text-danger': data.started_offers_count_percent < 100
              }">
                {{ data.started_offers_count_percent }}%
              </small>
            </td>
            <td>
              {{ data.new_customers_count }} <small>/ {{ data.target.new_customers_count }}</small><br>
              <small :class="{
                'text-success': data.new_customers_count_percent >= 100,
                'text-danger': data.new_customers_count_percent < 100
              }">
                {{ data.new_customers_count_percent }}%
              </small>
            </td>
            <td>
              {{ data.new_contacts_count }} <small>/ {{ data.target.new_contacts_count }}</small><br>
              <small :class="{
                'text-success': data.new_contacts_count_percent >= 100,
                'text-danger': data.new_contacts_count_percent < 100
              }">
                {{ data.new_contacts_count_percent }}%
              </small>
            </td>
            <!--<td>
              <small :class="{
                'text-success': data.avg_percent >= 100,
                'text-danger': data.avg_percent < 100
              }">
                {{ data.avg_percent }}%
              </small>
            </td>
            <td>
              {{ data.sum_percent }}%<br>
            </td>-->
          </tr>
          <tr v-if="kpiRealizations.data.length === 0">
            <td
              colspan="10"
              class="text-center">
              {{ $t('ui.noInfoToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <d-card style="width: 100%;" class="mt-5">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0 text-right">{{ $t('kpi.averageResults') }}</th>
            <th scope="col" class="border-0" style="width: 130px;"><small>{{ $t('kpi.contactsNumberRegardingNewOrders') }}</small></th>
            <th scope="col" class="border-0" style="width: 130px;"><small>{{ $t('kpi.numberOfStartedOrders') }}</small></th>
            <th scope="col" class="border-0" style="width: 130px;"><small>{{ $t('kpi.amountOfStartedOrders') }}</small></th>
            <th scope="col" class="border-0" style="width: 130px;"><small>{{ $t('kpi.numberOfStartedOffers') }}</small></th>
            <th scope="col" class="border-0" style="width: 130px;"><small>{{ $t('kpi.numberOfNewCustomers') }}</small></th>
            <th scope="col" class="border-0" style="width: 130px;"><small>{{ $t('kpi.numberOfNewContacts') }}</small></th>
            <!--<th scope="col" class="border-0" style="width: 80px;"></th>-->
            <!--<th scope="col" class="border-0" style="width: 100px;"></th>-->
          </tr>
        </thead>
        <tbody v-if="loaded">
          <tr
            v-for="(data, index) of kpiRealizations.stats"
            :key="'target-' + index">
            <td class="align-middle text-right">
              <template v-if="index === 'avg'">{{ $t('kpi.onAvergae') }}:</template>
              <template v-if="index === 'max'">{{ $t('ui.maximum') }}:</template>
              <template v-if="index === 'min'">{{ $t('ui.minimum') }}:</template>
            </td>
            <td>
              {{ data.new_orders_contacts_count }}<br>
              <small :class="{
                'text-success': data.new_orders_contacts_count_percent >= 100,
                'text-danger': data.new_orders_contacts_count_percent < 100
              }">
                {{ data.new_orders_contacts_count_percent }}%
              </small>
            </td>
            <td>
              {{ data.started_orders_count }}<br>
              <small :class="{
                'text-success': data.started_orders_count_percent >= 100,
                'text-danger': data.started_orders_count_percent < 100
              }">
                {{ data.started_orders_count_percent }}%
              </small>
            </td>
            <td>
              {{ data.started_orders_amount }}<br>
              <small :class="{
                'text-success': data.started_orders_amount_percent >= 100,
                'text-danger': data.started_orders_amount_percent < 100
              }">
                {{ data.started_orders_amount_percent }}%
              </small>
            </td>
            <td>
              {{ data.started_offers_count }}<br>
              <small :class="{
                'text-success': data.started_offers_count_percent >= 100,
                'text-danger': data.started_offers_count_percent < 100
              }">
                {{ data.started_offers_count_percent }}%
              </small>
            </td>
            <td>
              {{ data.new_customers_count }}<br>
              <small :class="{
                'text-success': data.new_customers_count_percent >= 100,
                'text-danger': data.new_customers_count_percent < 100
              }">
                {{ data.new_customers_count_percent }}%
              </small>
            </td>
            <td>
              {{ data.new_contacts_count }}<br>
              <small :class="{
                'text-success': data.new_contacts_count_percent >= 100,
                'text-danger': data.new_contacts_count_percent < 100
              }">
                {{ data.new_contacts_count_percent }}%
              </small>
            </td>
            <!--<td></td>
            <td></td>-->
          </tr>
          <tr v-if="kpiRealizations.stats.length === 0">
            <td
              colspan="10"
              class="text-center">
              {{ $t('ui.noInfoToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loaded" class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>
  </d-container>
</template>

<script>
import datepickerTranslations from './../data/datepicker-translations.js';
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasFilters from './../mixins/HasFilters.vue';
import getPrefferedLanguage from './../utils/i18nUtils.js';

export default {
  name: 'kpi-realization',
  mixins: [
    HasFilters
  ],
  computed: {
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    disableFiltersInSubview () {
      return true;
    }
  },
  data() {
    return {
      kpiRealizations: [],
      kpiStats: [],
      loaded: false,
      loadError: false,
      filters: {
        period: null
      }
    };
  },
  mounted () {
    if (typeof this.filters.period === 'string') {
      this.filters.period = new Date(this.filters.period);
    } else {
      this.filters.period = new Date();
    }

    this.loadFilteredData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.loaded = false;
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let whereData = {};

      if (this.filters.period !== null) {
        whereData.year = this.filters.period.getFullYear();
        whereData.month = this.filters.period.getMonth() + 1;
      }

      FormUtils.loadAdditionalData(this, {
        endpoint: '/kpi/realizations/items',
        outputKey: 'kpiRealizations',
        errorKey: 'loadError',
        loadedKey: 'loaded',
        noPagination: true,
        params: {
          where: whereData
        }
      });
    },
    validateFiltersForm () {
      FormUtils.validate(this.$refs['kpiRealizations-filters-form'], this.debouncedLoadFilteredData);
    }
  }
};
</script>

<style lang="scss">
.kpi-realizations .page-filters .vdp-datepicker .form-control[readonly] {
  background: #fff!important;
}
</style>
